import { createSlice } from "@reduxjs/toolkit";

const isMap = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

// The RBAC fields of the active winery, from a GET /users/me/rbac body:
//   permissions -> activeWineryPermissions (explicit grants, drive the menu)
//   effective   -> activeWineryEffectivePermissions (explicit + implied reads, what the server allows)
//   activeWineryRbacLoadedFor -> id of the winery the four fields were loaded for (null: never, or
//     cleared). A route guard waits while it differs from the active winery id: no verdict (and no
//     redirect to /unauthorized) on permissions that belong to another winery or are not there yet.
// An older server answers with the bare permission map: it is the explicit one, effective stays null.
const rbacFields = (payload) => {
    if (!isMap(payload)) return clearedRbac();
    if (!isMap(payload.permissions)) {
        return { ...clearedRbac(), activeWineryPermissions: payload };
    }
    return {
        activeWineryPermissions: payload.permissions,
        activeWineryEffectivePermissions: isMap(payload.effective) ? payload.effective : null,
        activeWineryGrantsAll: payload.grants_all === true,
        activeWineryRoleCodes: Array.isArray(payload.role_codes) ? payload.role_codes : [],
    };
};

function clearedRbac() {
    return {
        activeWineryPermissions: {},
        activeWineryEffectivePermissions: null,
        activeWineryGrantsAll: false,
        activeWineryRoleCodes: [],
        activeWineryRbacLoadedFor: null,
    };
}

export const userSlice = createSlice({
    name: 'user',
    initialState: {
        firstName: '',
        email: '',
        role: 'public',
        ...clearedRbac(),
    },
    reducers: {
        empty: state => {
            return {
                id: null,
                userName: '',
                clientName: '',
                role: -1,
                type: 'anonymous',
                global_roles: [],
                rbac_roles: [],
                settings: {},
                ...clearedRbac(),
            }
        },
        setUser: (state, action) => {
            const { UserInfo } = action.payload
            return {
                id: UserInfo?.id,
                role: UserInfo?.role,
                type: UserInfo?.type,
                userName: UserInfo?.username,
                clientName: UserInfo?.client_name,
                global_roles: UserInfo?.global_roles || [],
                rbac_roles: UserInfo?.rbac_roles || [],
                settings: UserInfo?.settings,
                activeWineryPermissions: state?.activeWineryPermissions || {},
                activeWineryEffectivePermissions: state?.activeWineryEffectivePermissions || null,
                activeWineryGrantsAll: state?.activeWineryGrantsAll === true,
                activeWineryRoleCodes: state?.activeWineryRoleCodes || [],
                activeWineryRbacLoadedFor: state?.activeWineryRbacLoadedFor ?? null,
            }
        },
        // The whole /users/me/rbac body: explicit, effective, grants_all, role_codes. The second
        // argument is the id of the winery it was requested for (activeWineryRbacLoadedFor).
        setUserRbac: {
            reducer: (state, action) => {
                Object.assign(state, rbacFields(action.payload), {
                    activeWineryRbacLoadedFor: action.meta?.wineryId ?? null,
                });
            },
            prepare: (body, wineryId) => ({ payload: body, meta: { wineryId } }),
        },
        // The request for the permissions of a winery failed: the loading ends, so a guard shows
        // /unauthorized instead of waiting for ever. Nothing is granted, unless the store already holds
        // the permissions of that same winery (a refresh that failed): those are kept.
        setUserRbacFailed: (state, action) => {
            const wineryId = action.payload ?? null;
            if (wineryId !== null && state.activeWineryRbacLoadedFor === wineryId) return;
            Object.assign(state, clearedRbac(), { activeWineryRbacLoadedFor: wineryId });
        },
        // Explicit map only. Prefer setUserRbac: the effective map is dropped here because it
        // would no longer match the explicit one.
        setUserRbacPermissions: (state, action) => {
            state.activeWineryPermissions = action.payload || {};
            state.activeWineryEffectivePermissions = null;
        },
        // A setting saved from the settings page, until the next token brings it from the server.
        setUserSetting: (state, action) => {
            const { key, value } = action.payload;
            state.settings = { ...(state.settings || {}), [key]: value };
        }
    }
});

export const { empty, setUser, setUserRbac, setUserRbacFailed, setUserRbacPermissions, setUserSetting } = userSlice.actions;

export default userSlice.reducer